import { ProductInterface } from '../../types/products/ProductInterface';
import { ProductEanDto } from '../../types/products/ProductEanInterface';
import { ProductFileType } from '../../types/products/ProductFileType';
import { FileCreatorRole } from '../../types/receivings/FileCreatorRole';
import { ScanImeiSnType } from '../../constants/scanImeiSnType';
import { GridInput } from '../grid';

/** GWMS product attachment (maps to Core FILES; type is instruction | label | other). */
export interface ProductFileInput {
    id?: number;
    type: ProductFileType;
    data?: string;
    fileName: string;
    fileType: string;
    fileSize?: string;
    createdAt?: Date | string | null;
    creatorRole?: FileCreatorRole | null;
}

export interface Product {
    id: number;
    sku: string;
    guid: string | null;
    name: string;
    description: string | null;
    price: number | null;
    hsCode: string | null;
    upc: string | null;
    ean: string | null;
    /** All EANs of the product (active + inactive). Nested — not a grid column. */
    eans?: ProductEanDto[];
    /** Read-only: 1 = EANs are managed by Skladon, eshop writes to ean/eans are ignored. */
    eanLockedByDcix?: number;
    rop: number;
    expMethod: ProductInterface['EXP_METHOD'];
    mpn: string | null;
    isbn: string | null;
    note: string | null;
    productType: ProductInterface['PRODUCT_TYPE'];
    customField1: string | null;
    customField2: string | null;
    customField3: string | null;
    ageCheck: boolean;
    active: boolean;
    scanSerialNumber: boolean; // @deprecated Use scanImeiSnType instead
    scanImeiSn?: 0 | 1 | 2; // @deprecated Use scanImeiSnType. 0=No, 1=Serial Numbers, 2=IMEI (expedition only)
    /** Codebook code: none, snExpedition, imeiExpedition, snInbound, imeiInbound, snBoth, imeiBoth. */
    scanImeiSnType?: ScanImeiSnType;
    originCountry: string | null;
    isDeleted?: boolean;
    batteryType: string | null;
    snRegex: string;
    unitPrice: string | null;
    weight: number | null;
    width: number | null;
    height: number | null;
    length: number | null;
    insertToProductSkus: string[];
    insertToCountries: string[];
    insertType: string | null;
    insertByOrderType: 'all' | 'b2c' | 'b2b' | null;
    insertPriceFrom: number;
    insertPriceTo: number;
    expirationDays: number;
    createdAt: string;
    updatedAt: string;
    dimensionsChangedAt: Date | null;
    deletedAt: Date | null;
    quantityConversion: number;
    /** System the product record originated from. */
    sourceSys?: string | null;
    files?: ProductFileInput[];
}

export interface ProductAvailability {
    sku: string;
    available: number | null;
    all: number | null;
    onHoldQuantity: number | null;
    dutyQuantity: number | null;
    onExpedition: number | null;
    allocated: number | null;
    awaiting: number | null;
    error: string | null;
}

export type ProductAvailabilityCount = ProductAvailability & {
    items?: (Omit<ProductAvailability, 'error' | 'sku'> & { expirationDate: string | null; lot: string | null })[];
};

export interface ProductAvailabilityInput {
    skus: string[];
}

export interface ProductsGridOutput {
    data: Product[];
    request: GridInput;
    totalCount: number;
}

export const fromCoreProductNames = {
    ID: 'id',
    CUSTOM_FIELD_1: 'customField1',
    CUSTOM_FIELD_2: 'customField2',
    CUSTOM_FIELD_3: 'customField3',
    DESCRIPTION: 'description',
    EAN: 'ean',
    EANS: 'eans',
    EAN_LOCKED_BY_DCIX: 'eanLockedByDcix',
    EXP_METHOD: 'expMethod',
    GUID: 'guid',
    HS_CODE: 'hsCode',
    ISBN: 'isbn',
    MPN: 'mpn',
    NAME: 'name',
    NOTE: 'note',
    PRICE: 'price',
    PRODUCT_TYPE: 'productType',
    ROP: 'rop',
    SKU: 'sku',
    UPC: 'upc',
    ACTIVE: 'active',
    SCAN_SERIAL_NUMBER: 'scanSerialNumber',
    SCAN_IMEI_SN_TYPE: 'scanImeiSnType',
    ORIGIN_COUNTRY: 'originCountry',
    SN_REGEX: 'snRegex',
    UNIT_PRICE: 'unitPrice',
    WEIGHT: 'weight',
    WIDTH: 'width',
    HEIGHT: 'height',
    LENGTH: 'length',
    INSERT_TO_PRODUCT_ID: 'insertToProductId',
    INSERT_TYPE: 'insertType',
    INSERT_BY_ORDER_TYPE: 'insertByOrderType',
    INSERT_PRICE_FROM: 'insertPriceFrom',
    INSERT_PRICE_TO: 'insertPriceTo',
    EXPIRATION_DAYS: 'expirationDays',
    CREATED_AT: 'createdAt',
    UPDATED_AT: 'updatedAt',
    DIMENSIONS_CHANGED_AT: 'dimensionsChangedAt',
    DELETED_AT: 'deletedAt',
    QUANTITY_CONVERSION: 'quantityConversion',
    IS_DELETED: 'isDeleted',
    AGECHECK: 'ageCheck',
    BATTERY_TYPE: 'batteryType',
    SOURCE_SYS: 'sourceSys',
};

export const toCoreProductNames = {
    id: 'ID',
    customField1: 'CUSTOM_FIELD_1',
    customField2: 'CUSTOM_FIELD_2',
    customField3: 'CUSTOM_FIELD_3',
    description: 'DESCRIPTION',
    ean: 'EAN',
    eanLockedByDcix: 'EAN_LOCKED_BY_DCIX',
    expMethod: 'EXP_METHOD',
    guid: 'GUID',
    hsCode: 'HS_CODE',
    isbn: 'ISBN',
    mpn: 'MPN',
    name: 'NAME',
    note: 'NOTE',
    price: 'PRICE',
    productType: 'PRODUCT_TYPE',
    rop: 'ROP',
    sku: 'SKU',
    upc: 'UPC',
    active: 'ACTIVE',
    scanSerialNumber: 'SCAN_SERIAL_NUMBER',
    scanImeiSn: 'SCAN_SERIAL_NUMBER',
    scanImeiSnType: 'SCAN_IMEI_SN_TYPE',
    originCountry: 'ORIGIN_COUNTRY',
    snRegex: 'SN_REGEX',
    unitPrice: 'UNIT_PRICE',
    weight: 'WEIGHT',
    width: 'WIDTH',
    height: 'HEIGHT',
    length: 'LENGTH',
    insertToProductId: 'INSERT_TO_PRODUCT_ID',
    insertType: 'INSERT_TYPE',
    insertByOrderType: 'INSERT_BY_ORDER_TYPE',
    insertPriceFrom: 'INSERT_PRICE_FROM',
    insertPriceTo: 'INSERT_PRICE_TO',
    expirationDays: 'EXPIRATION_DAYS',
    createdAt: 'CREATED_AT',
    updatedAt: 'UPDATED_AT',
    dimensionsChangedAt: 'DIMENSIONS_CHANGED_AT',
    deletedAt: 'DELETED_AT',
    quantityConversion: 'QUANTITY_CONVERSION',
    isDeleted: 'IS_DELETED',
    ageCheck: 'AGECHECK',
    batteryType: 'BATTERY_TYPE',
    sourceSys: 'SOURCE_SYS',
};

const { scanSerialNumber: _scanSerialNumberColumn, ...toCoreProductNamesWithoutScanSerialNumber } =
    toCoreProductNames;

/** v2.1 grid columns — `scanImeiSnType` replaces deprecated `scanSerialNumber` / `scanImeiSn`. */
export const toCoreProductNamesV21 = {
    ...toCoreProductNamesWithoutScanSerialNumber,
    scanImeiSn: 'SCAN_SERIAL_NUMBER',
    scanImeiSnType: 'SCAN_IMEI_SN_TYPE',
};

export const fromCoreProductNamesV21 = {
    ...fromCoreProductNames,
    SCAN_SERIAL_NUMBER: 'scanImeiSn',
    SCAN_IMEI_SN_TYPE: 'scanImeiSnType',
};
